import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody, Table, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup, Alert, Row, Col } from 'reactstrap';
import { useTranslation } from 'react-multi-lang';
import { useToasts } from 'react-toast-notifications';
import QuarriesService from '../Services/QuarriesService';
import ShelfPolygonEditor from './ShelfPolygonEditor';
import QuarryMapView from './QuarryMapView';
import { SystemService } from '../Services/SystemService';
import AuthService from '../Services/AuthService';

const Quarries = () => {
    const t = useTranslation();
    const { addToast } = useToasts();
    const [activeQuarry, setActiveQuarry] = useState(null);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [activeShelf, setActiveShelf] = useState(null);
    const [currentConfig, setCurrentConfig] = useState(null);
    const [shelvesFromConfig, setShelvesFromConfig] = useState([]);

    const quarriesService = new QuarriesService();
    const systemService = new SystemService();
    const authService = new AuthService();
    const baseUrl = systemService.getBaseUrl().replace(/\/$/, '');

    useEffect(() => {
        const checkConfig = setInterval(() => {
            const config = authService.getConfig();
            if (!currentConfig || config.whCode !== currentConfig.whCode) {
                setCurrentConfig(config);
                setShelvesFromConfig(config.shelves || []);
                fetchQuarry(config.whCode);
            }
        }, 1000);
        return () => clearInterval(checkConfig);
    }, [currentConfig]);

    const fetchQuarry = (whCode) => {
        quarriesService.getQuarryByWhCode(whCode).then(r => {
            setActiveQuarry(r.data);
        }).catch(e => {
            console.error(e);
            setActiveQuarry(null);
        });
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const id = activeQuarry ? activeQuarry.id : `whCode:${currentConfig.whCode}`;
        quarriesService.uploadMap(id, file, currentConfig.name).then(r => {
            addToast(t('Ο χάρτης ανέβηκε'), { appearance: 'success', autoDismiss: true });
            fetchQuarry(currentConfig.whCode);
        });
    };


    const openEditor = (shelf) => {
        setActiveShelf(shelf);
        setIsEditorOpen(true);
    };

    const savePolygon = (points) => {
        quarriesService.updateShelf(activeShelf.id, { polygon: points }).then(r => {
            addToast(t('Το πολύγωνο αποθηκεύτηκε'), { appearance: 'success', autoDismiss: true });
            fetchQuarry(currentConfig.whCode);
            setIsEditorOpen(false);
        });
    };

    const deleteShelf = (id) => {
        if (window.confirm(t('Θέλετε σίγουρα να καθαρίσετε τα δεδομένα αυτού του παταριού;'))) {
            quarriesService.deleteShelf(id).then(() => {
                addToast(t('Τα δεδομένα διεγράφησαν'), { appearance: 'success', autoDismiss: true });
                fetchQuarry(currentConfig.whCode);
            });
        }
    };

    const handleAutoAddAndOpenEditor = (shelfName) => {
        const id = activeQuarry ? activeQuarry.id : `whCode:${currentConfig.whCode}`;
        quarriesService.addShelf(id, { name: shelfName }, currentConfig.name).then(r => {
            quarriesService.getQuarryByWhCode(currentConfig.whCode).then(res => {
                setActiveQuarry(res.data);
                const newlyCreatedShelf = res.data.shelves.find(s => s.name === shelfName);
                if (newlyCreatedShelf) {
                    openEditor(newlyCreatedShelf);
                }
            });
        });
    };


    if (!currentConfig) return <div className="p-4"><Alert color="info">Παρακαλώ επιλέξτε λατομείο από το μενού επάνω.</Alert></div>;

    return (
        <div className="p-4">
            <Card>
                <CardHeader className="d-flex justify-content-between align-items-center bg-primary text-white">
                    <h4>Διαχείριση Λατομείου: {currentConfig.name}</h4>
                </CardHeader>
                <CardBody>
                    {isEditorOpen && activeQuarry && activeShelf ? (
                        <ShelfPolygonEditor 
                            imageUrl={`${baseUrl}/uploads/topology-maps/${activeQuarry.topologyMap}`}
                            shelfName={activeShelf.name}
                            polygon={activeShelf.polygon}
                            onSave={savePolygon}
                            onCancel={() => setIsEditorOpen(false)}
                        />
                    ) : (
                        <div>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <h5>Χάρτης (Schema)</h5>
                                    {activeQuarry && activeQuarry.topologyMap ? (
                                        <div className="d-flex align-items-center">
                                            <span className="me-2 text-success">✓ Ανέβηκε</span>
                                            <Input type="file" onChange={handleFileUpload} />
                                        </div>
                                    ) : (
                                        <div>
                                            <Alert color="warning">Δεν έχει ανέβει χάρτης για αυτό το λατομείο.</Alert>
                                            <Input type="file" onChange={handleFileUpload} />
                                        </div>
                                    )}
                                </Col>
                                <Col md={6} className="text-end">
                                    {!activeQuarry && (
                                        <div className="small text-muted mt-2">
                                            * Το λατομείο θα δημιουργηθεί αυτόματα κατά το πρώτο "Save" (ανέβασμα χάρτη ή σχεδίαση παταριού).
                                        </div>
                                    )}
                                </Col>
                            </Row>

                            {activeQuarry && activeQuarry.topologyMap && (
                                <div className="mb-4">
                                    <QuarryMapView 
                                        imageUrl={`${baseUrl}/uploads/topology-maps/${activeQuarry.topologyMap}`}
                                        shelves={activeQuarry.shelves || []}
                                        onSelectShelf={openEditor}
                                    />
                                </div>
                            )}

                            <hr />

                            <h5>Πατάρια (Shelves)</h5>
                            {!shelvesFromConfig || shelvesFromConfig.length === 0 ? (
                                <p>Δεν υπάρχουν πατάρια στην ρύθμιση.</p>
                            ) : (
                                <Table responsive hover striped>
                                    <thead>
                                        <tr>
                                            <th>Όνομα</th>
                                            <th>Πολύγωνο</th>
                                            <th>Ενέργειες</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {shelvesFromConfig.map(shelfName => {
                                            const s = activeQuarry?.shelves?.find(qs => qs.name === shelfName);
                                            return (
                                                <tr key={shelfName}>
                                                    <td>{shelfName}</td>
                                                    <td>{s && s.polygon ? '📍 Σχεδιασμένο' : '❌ Δεν έχει σχεδιαστεί'}</td>
                                                    <td>
                                                        <Button 
                                                            size="sm" 
                                                            color="info" 
                                                            className="me-2" 
                                                            onClick={() => s ? openEditor(s) : handleAutoAddAndOpenEditor(shelfName)} 
                                                            disabled={!activeQuarry || !activeQuarry.topologyMap}
                                                        >
                                                            📐 Σχεδίαση
                                                        </Button>
                                                        {s && (
                                                            <Button size="sm" color="danger" onClick={() => deleteShelf(s.id)}>Καθαρισμός</Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>

        </div>
    );
};

export default Quarries;
